/**
 * Bounded waits on Segment.
 *
 * A request that a tracker blocker black-holes never settles, and
 * analytics-next's pre-init buffer never flushes if the SDK settings fetch is
 * blocked — so anything that awaits the SDK (`analytics.load()`,
 * `analytics.user()`, a delivery probe) needs a bound or it stalls the caller
 * indefinitely. These helpers are that bound.
 *
 * Exposed as `@hungryroot/segment/with-timeout` rather than from the main
 * entry: the main entry boots the SDK as a module side effect, and consumers
 * that only need a timeout should not pay for (or have to mock) that.
 */

/**
 * Races `work` against a timer. Rejects with
 * `"<label> did not settle within <timeoutMs>ms"` if the timer wins; otherwise
 * settles exactly as `work` does. The timer is always cleared.
 *
 * `work` itself is untouched — it may still settle later, and a caller that
 * cares can observe it directly (see `observeSegmentLoad`).
 */
export async function withTimeout<T>(
  work: PromiseLike<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} did not settle within ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * `withTimeout` for callers that only want the value if it arrives in time:
 * resolves `null` on timeout *and* on rejection, never throws. For values that
 * are nice-to-have (an analytics join key) rather than load-bearing.
 */
export async function withTimeoutOrNull<T>(
  work: PromiseLike<T>,
  timeoutMs: number,
  label: string
): Promise<T | null> {
  try {
    return await withTimeout(work, timeoutMs, label);
  } catch {
    return null;
  }
}
